/**
 * Telemetry Service
 *
 * Wraps the Application Insights browser SDK, providing a single initialisation
 * point and safe `track*` helpers that no-op when telemetry has not been
 * configured.
 *
 * Every exported function is safe to call before (or without) initialisation,
 * so callers never guard. That matters because telemetry is opt-in per
 * environment: `MSS_TELEMETRY_ENABLED` may be off, or the backend may have no
 * connection string to hand out, and neither case should reach a caller.
 *
 * Reports the same run the Sentry side does, from the same `PerfReport` and
 * `StylingRunStats` — see `trackStylingRun`.
 *
 * Caught errors are Sentry's side of that split and are not duplicated here; a
 * failed run still reaches App Insights as `outcome`/`failedStep` on the run
 * event. Uncaught errors and unhandled rejections are collected by the SDK.
 */

/* global Office, console, navigator, performance, __MSS_ENVIRONMENT__, __MSS_APP_VERSION__ */

import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";

import { selfMs } from "src/services/perf";

import type { ITelemetryItem } from "@microsoft/applicationinsights-web";
import type { PerfMeta, PerfReport } from "src/services/perf";
import type { StylingRunStats } from "src/services/sentry-reporting";

let appInsights: ApplicationInsights | null = null;
const reactPlugin = new ReactPlugin();

/**
 * Build identity and Office host, stamped onto every item.
 *
 * Office diagnostics are only available after `Office.onReady`, and absent
 * entirely in tests and in the auth dialog, so every field is optional.
 */
function commonProperties(): Record<string, string> {
  // `typeof` rather than `Office?.` — optional chaining still throws a
  // ReferenceError when the identifier itself was never declared.
  const diagnostics = typeof Office === "undefined" ? undefined : Office.context?.diagnostics;
  return {
    // Deliberately the same strings Sentry reports as `release` and
    // `environment`, so a slow run here and an issue there can be tied to one
    // build. Empty release in a local build, where CI supplied no version.
    release: __MSS_APP_VERSION__ ? `mss@${__MSS_APP_VERSION__}` : "",
    environment: __MSS_ENVIRONMENT__,
    // `platform` and `host` are enums, not strings.
    officePlatform: String(diagnostics?.platform ?? "unknown"),
    officeVersion: diagnostics?.version ?? "unknown",
    officeHost: String(diagnostics?.host ?? "unknown"),
    userAgent: navigator?.userAgent ?? "unknown",
  };
}

/**
 * Initialise the Application Insights SDK.
 *
 * Should be called once during app start-up after the connection string has
 * been fetched from the backend config endpoint. If called more than once the
 * subsequent calls are ignored.
 *
 * @param connectionString - Application Insights connection string
 */
export function initializeTelemetry(connectionString: string): void {
  if (appInsights) {
    return; // Already initialised.
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        extensions: [reactPlugin],
        enableAutoRouteTracking: false, // No client-side routing in the add-in.
        // The apply pipeline is almost entirely async around `Word.run`, so an
        // unhandled rejection is a realistic failure mode. Unlike `window.onerror`
        // exceptions, these are not collected by default.
        enableUnhandledPromiseRejectionTracking: true,
        // Deliberately left at its default (false): correlation headers would
        // make every API call preflight, and the API has no CORS allow_headers
        // configured to permit them. Runs are correlated by `runId` instead,
        // which is the same id Sentry uses as its trace id.
        // enableCorsCorrelation: false,
      },
    });

    appInsights.loadAppInsights();

    // Stamped once here rather than per call site, so no event has to remember
    // to describe the build it came from.
    const common = commonProperties();
    appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
      item.data = { ...common, ...item.data };
    });

    // The SDK sends no page view of its own, and the portal's Browser blades
    // (Performance → Browser, Usage → Users/Sessions) read from `pageViews`, so
    // without this they stay permanently blank. One view, not a route history:
    // the taskpane has no pages to compare. Must follow the initializer above,
    // or this one item would ship without the release and environment stamp.
    appInsights.trackPageView({ name: "Taskpane" });
  } catch (err) {
    // A malformed connection string must not stop the add-in from mounting.
    appInsights = null;
    console.warn("[telemetry] initialisation failed; telemetry disabled:", err);
  }
}

/**
 * Associate subsequent telemetry with a user.
 *
 * @param userId - Opaque Okta `sub` claim. Never an email address: this value
 *   is stored by Azure and shown in the portal.
 */
export function setUserContext(userId: string): void {
  // `storeInCookie: false` — cookies are unreliable in the Office webview, and
  // an identity cookie is not something we want written regardless.
  appInsights?.setAuthenticatedUserContext(userId, undefined, false);
}

/**
 * Track a custom event in Application Insights.
 *
 * Silently no-ops when telemetry has not been initialised, so callers do not
 * need to guard against the uninitialised state.
 *
 * @param name - Event name (e.g. "StyleChange_Approve")
 * @param properties - Optional string key/value properties
 * @param measurements - Optional numeric values. These must be passed here
 *   rather than as properties: only measurements are aggregatable in KQL, so a
 *   duration sent as a property cannot be averaged or percentiled. The SDK's
 *   types mark this field deprecated, but it is not — `EventEnvelopeCreator`
 *   reads `baseData.measurements` directly and it is the only way to set
 *   `customMeasurements` explicitly rather than by value-type guessing.
 */
export function trackEvent(
  name: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
): void {
  appInsights?.trackEvent({ name, properties, measurements });
}

/**
 * Time an operation and report its duration, on both success and failure.
 * The error is always rethrown — this observes, it never swallows.
 */
export async function trackTimedAction<T>(
  name: string,
  properties: Record<string, string>,
  fn: () => Promise<T>
): Promise<T> {
  const start = performance.now();
  let status = "success";
  try {
    return await fn();
  } catch (err) {
    status = "error";
    throw err;
  } finally {
    trackEvent(name, { ...properties, status }, { durationMs: performance.now() - start });
  }
}

/**
 * Split an untyped bag into the two buckets App Insights needs: numbers are
 * only aggregatable in KQL as measurements, everything else is a property.
 */
function partition(source: PerfMeta | Record<string, unknown> | undefined): {
  properties: Record<string, string>;
  measurements: Record<string, number>;
} {
  const properties: Record<string, string> = {};
  const measurements: Record<string, number> = {};
  for (const [key, value] of Object.entries(source ?? {})) {
    if (value === undefined || value === null) {
      continue;
    }
    if (typeof value === "number") {
      measurements[key] = value;
    } else {
      properties[key] = String(value);
    }
  }
  return { properties, measurements };
}

/**
 * Report a completed styling run as one run-level event plus one event per step.
 *
 * Takes the same two arguments as `captureStylingRunCompleted`, so the two sinks
 * cannot drift in what they describe. `stats.runId` is Sentry's trace id, which
 * makes it the join key between an App Insights event and a Sentry trace.
 *
 * Steps carry `selfMs` as well as `durationMs` because a `stage:*` entry
 * encloses every sync it wraps: durations do not sum to the run total, and
 * ranking or computing percentage shares from `durationMs` double-counts.
 */
export function trackStylingRun(report: PerfReport, stats: StylingRunStats): void {
  if (!appInsights) return;

  const runId = stats.runId ?? "";
  const run = partition(stats as unknown as Record<string, unknown>);

  trackEvent(
    `${report.label}_Complete`,
    { ...run.properties, runId },
    {
      ...run.measurements,
      totalMs: report.totalMs,
      unattributedMs: report.unattributedMs,
      stepCount: report.entries.length,
    }
  );

  report.entries.forEach((entry, index) => {
    const meta = partition(entry.meta);
    trackEvent(
      `${report.label}_Step`,
      { ...meta.properties, runId, step: entry.name },
      {
        ...meta.measurements,
        durationMs: entry.durationMs,
        selfMs: selfMs(report.entries, index),
        atMs: entry.atMs,
      }
    );
  });
}

/** Expose the ReactPlugin instance for the AppInsightsContext provider. */
export function getReactPlugin(): ReactPlugin {
  return reactPlugin;
}
